import { useCallback, useEffect, useRef, useState } from 'react';
import { EMPTY_INSETS, layoutKey } from '../../../utils/mapInsets';

/**
 * Measures how much of the map wrapper is covered by overlays (filter strip, legends, hazard panel / sheet)
 * so popups and fits can use the free rectangle. All insets are in CSS px relative to the wrapper.
 * Attach the four `set*El` callbacks as refs; each is optional.
 */
export function useMapInsets() {
  const [insets, setInsets] = useState(EMPTY_INSETS);
  const insetsRef = useRef(EMPTY_INSETS);
  const els = useRef({ wrapper: null, top: null, bottom: null, panel: null });
  const observerRef = useRef(null);

  const measure = useCallback(() => {
    const { wrapper, top, bottom, panel } = els.current;
    if (!wrapper) return;
    const w = wrapper.getBoundingClientRect();
    const isDesktop = !!window.matchMedia?.('(min-width: 640px)').matches;
    const next = {
      top: 0, right: 0, bottom: 0, left: 0, width: Math.round(w.width), height: Math.round(w.height),
    };
    if (top) next.top = Math.max(0, Math.round(top.getBoundingClientRect().bottom - w.top));
    if (bottom) next.bottom = Math.max(0, Math.round(w.bottom - bottom.getBoundingClientRect().top));
    if (panel) {
      const p = panel.getBoundingClientRect();
      if (isDesktop) next.left = Math.max(0, Math.round(p.right - w.left));      // docked flush left
      else next.bottom = Math.max(next.bottom, Math.round(w.bottom - p.top));
    }
    if (layoutKey(next) !== layoutKey(insetsRef.current)) {
      insetsRef.current = next;
      setInsets(next);
    }
  }, []);

  // One observer for all four elements; re-observe whenever one is attached or detached.
  const observe = useCallback(() => {
    const ro = observerRef.current;
    if (ro) {
      ro.disconnect();
      Object.values(els.current).forEach((el) => { if (el) ro.observe(el); });
    }
    measure();
  }, [measure]);

  const attach = useCallback((key, el) => {
    if (els.current[key] === el) return;
    els.current[key] = el;
    observe();
  }, [observe]);
  const setWrapperEl = useCallback((el) => attach('wrapper', el), [attach]);
  const setTopEl = useCallback((el) => attach('top', el), [attach]);
  const setBottomEl = useCallback((el) => attach('bottom', el), [attach]);
  const setPanelEl = useCallback((el) => attach('panel', el), [attach]);

  useEffect(() => {
    if (typeof ResizeObserver !== 'undefined') {
      observerRef.current = new ResizeObserver(measure);
      Object.values(els.current).forEach((el) => { if (el) observerRef.current.observe(el); });
    }
    const mq = window.matchMedia?.('(min-width: 640px)');
    window.addEventListener('resize', measure);
    mq?.addEventListener?.('change', measure);
    measure();
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      window.removeEventListener('resize', measure);
      mq?.removeEventListener?.('change', measure);
    };
  }, [measure]);

  return { insets, setWrapperEl, setTopEl, setBottomEl, setPanelEl };
}
